import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Crown, CheckCircle2, Zap, Loader2, Shield
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
const TIER_STYLES = {
  basic: { border: 'border-gray-300', glow: '', icon: Shield, iconColor: 'text-gray-600', bg: '' },
  pro: { border: 'border-pink-400', glow: 'shadow-[0_0_20px_rgba(236,72,153,0.2)]', icon: Zap, iconColor: 'text-pink-600', bg: 'bg-gradient-to-b from-pink-50/50 to-white' },
  elite: { border: 'border-amber-400', glow: 'shadow-[0_0_25px_rgba(245,158,11,0.3)]', icon: Crown, iconColor: 'text-amber-600', bg: 'bg-gradient-to-b from-amber-50/50 to-white' },
};

const TalentPremium = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    if (searchParams.get('session_id')) {
      toast.success('Welcome to Premium! Your visibility just increased.');
    }
    if (searchParams.get('boost') === 'success') {
      const sessionId = searchParams.get('session_id');
      // Fallback activation path — call the confirm endpoint so `is_featured`
      // flips immediately even if the Stripe webhook hasn't landed yet. This
      // prevents the "I paid but I don't look boosted" race.
      const activate = async () => {
        if (sessionId) {
          try {
            await api.post(`/talent-premium/boost/confirm/${sessionId}`);
          } catch (err) {
            // Webhook will still activate it eventually — just warn.
            console.warn('Boost confirm failed; webhook will catch up:', err);
          }
        }
        toast.success("Boost activated! You're now more visible.");
        fetchData();
      };
      activate();
    } else {
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, statusRes] = await Promise.all([
        api.get('/talent-premium/tiers'),
        api.get('/talent-premium/my-status'),
      ]);
      setTiers(tiersRes.data.tiers || []);
      setBoosts(tiersRes.data.boosts || []);
      setStatus(statusRes.data);
    } catch {
      toast.error('Failed to load premium info');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const res = await api.post('/talent-premium/checkout', { tier: tierId });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('No checkout URL returned from server');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to start checkout';
      toast.error(`Checkout failed: ${detail}`);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancel = async () => {
    try {
      await api.post('/talent-premium/cancel');
      toast.success('Subscription will cancel at end of billing period');
      fetchData();
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const handleBuyBoost = async (boostId) => {
    setCheckoutLoading(`boost-${boostId}`);
    try {
      const res = await api.post('/talent-premium/buy-boost', { boost_id: boostId });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch {
      toast.error('Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-premium-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/talent/home')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-900">Upgrade to Premium</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Crown className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Get More Visibility. Get More Bookings.</h2>
          <p className="text-gray-500 max-w-md mx-auto">Appear first in search, stand out in discovery, and get noticed by clients faster.</p>
        </div>

        {/* Current Status */}
        {status?.is_premium && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between" data-testid="current-status">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Premium Active — {status.tier_name || 'Premium'}</p>
                {status.period_end && <p className="text-xs text-green-600">Renews {formatDate(status.period_end)}</p>}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 text-xs" onClick={handleCancel} data-testid="cancel-btn">Cancel</Button>
          </div>
        )}

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="tier-cards">
          {tiers.map((tier) => {
            const style = TIER_STYLES[tier.id] || TIER_STYLES.basic;
            const TierIcon = style.icon;
            const isCurrentTier = status?.current_tier === tier.id;
            return (
              <Card
                key={tier.id}
                className={`relative overflow-hidden border-2 ${style.border} ${style.glow} ${style.bg} ${tier.popular ? 'scale-[1.02]' : ''}`}
                data-testid={`tier-card-${tier.id}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-center text-[10px] font-bold py-1 uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <CardContent className={`p-6 ${tier.popular ? 'pt-9' : ''}`}>
                  <div className="text-center mb-4">
                    <TierIcon className={`w-8 h-8 mx-auto mb-2 ${style.iconColor}`} />
                    <h3 className="font-bold text-lg text-gray-900">{tier.name}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-gray-900">${tier.price}</span>
                      <span className="text-sm text-gray-500">/mo</span>
                    </div>
                  </div>

                  <div className="space-y-2.5 mb-6">
                    {tier.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{f}</span>
                      </div>
                    ))}
                  </div>

                  {isCurrentTier ? (
                    <Button disabled className="w-full" variant="outline">Current Plan</Button>
                  ) : (
                    <Button
                      className={`w-full ${tier.popular ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600' : ''}`}
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={checkoutLoading === tier.id}
                      data-testid={`upgrade-${tier.id}`}
                    >
                      {checkoutLoading === tier.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {status?.is_premium ? 'Switch Plan' : 'Upgrade'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">Cancel anytime. No commitment. Your visibility increases immediately.</p>
          <p className="text-[10px] text-gray-300 mt-1">Premium boosts visibility but does not guarantee bookings.</p>
        </div>

        {/* One-Time Boosts Section */}
        <div className="mt-10 pt-8 border-t border-gray-200" data-testid="boost-section">
          <div className="text-center mb-6">
            <Zap className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
            <h3 className="text-lg font-bold text-gray-900">Just Need a Quick Boost?</h3>
            <p className="text-sm text-gray-500">Get seen for a limited time — no subscription required</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            {boosts.map((boost) => (
              <Card key={boost.id} className="border-cyan-200 hover:border-cyan-400 transition-all" data-testid={`boost-card-${boost.id}`}>
                <CardContent className="p-5 text-center">
                  <Zap className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900">{boost.label}</h4>
                  <p className="text-2xl font-bold text-gray-900 mt-1">${boost.price}</p>
                  <p className="text-xs text-gray-500 mb-4">one-time</p>
                  <Button
                    variant="outline"
                    className="w-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    onClick={() => handleBuyBoost(boost.id)}
                    disabled={checkoutLoading === `boost-${boost.id}`}
                    data-testid={`buy-boost-${boost.id}`}
                  >
                    {checkoutLoading === `boost-${boost.id}` ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Get Boosted
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Upgrade Prompt */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center max-w-lg mx-auto" data-testid="upgrade-prompt">
            <p className="text-sm text-amber-800">
              Want this visibility <strong>all month</strong>? <span className="text-amber-700">Upgrade to Premium for just $14.99/mo</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentPremium;
